import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  getCurrentUser,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  type AuthUser,
} from '@aws-amplify/auth';
import { authConfigured, devAuth } from './config';

export type Role = 'admin' | 'team-admin' | 'scorekeeper' | 'fan';

interface AuthState {
  ready: boolean;
  user: AuthUser | undefined;
  email: string | undefined;
  groups: Role[];
  idToken: string | undefined;
  // Pending challenge state when sign-in returns NEW_PASSWORD_REQUIRED.
  challenge: 'NEW_PASSWORD_REQUIRED' | undefined;
}

// Cached snapshot of (email, groups) from the last successful auth refresh.
// Used to render the navbar with no layout flash on subsequent loads while
// the async Cognito refresh runs. Evicted on signOut.
const SNAPSHOT_KEY = 'baseballstrm.authSnapshot.v1';

interface AuthSnapshot {
  email?: string;
  groups: Role[];
}

function readSnapshot(): AuthSnapshot | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(SNAPSHOT_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as AuthSnapshot;
    if (!parsed || !Array.isArray(parsed.groups)) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

function writeSnapshot(snap: AuthSnapshot | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    if (snap) {
      window.localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snap));
    } else {
      window.localStorage.removeItem(SNAPSHOT_KEY);
    }
  } catch {
    // localStorage can be disabled in some browser modes; the navbar will
    // just flash on first paint, which is acceptable.
  }
}

interface AuthApi extends AuthState {
  signIn: (email: string, password: string) => Promise<{ challenge?: string }>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ needsConfirmation: boolean }>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthApi | undefined>(undefined);

const parseGroups = (raw: unknown): Role[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as Role[];
  if (typeof raw === 'string') {
    return raw
      .replace(/^\[|\]$/g, '')
      .split(/[,\s]+/)
      .filter(Boolean) as Role[];
  }
  return [];
};

export function AuthProvider({ children }: { children: ReactNode }) {
  // Seed from the previous session's cached snapshot so the navbar (and any
  // role-gated UI in non-fetching components) renders the correct shape on
  // first paint. `ready: false` keeps consumers that *need* a verified
  // session (admin route guards, API calls) from acting on stale state.
  const [state, setState] = useState<AuthState>(() => {
    if (devAuth) {
      return {
        ready: true,
        user: { username: devAuth.email, userId: 'dev-auth' } as AuthUser,
        email: devAuth.email,
        groups: parseGroups(devAuth.groups),
        idToken: 'dev-auth',
        challenge: undefined,
      };
    }
    const snap = readSnapshot();
    return {
      ready: false,
      user: snap ? ({} as AuthUser) : undefined,
      email: snap?.email,
      groups: snap?.groups ?? [],
      idToken: undefined,
      challenge: undefined,
    };
  });

  const refresh = useCallback(async () => {
    if (devAuth) {
      setState({
        ready: true,
        user: { username: devAuth.email, userId: 'dev-auth' } as AuthUser,
        email: devAuth.email,
        groups: parseGroups(devAuth.groups),
        idToken: 'dev-auth',
        challenge: undefined,
      });
      return;
    }
    if (!authConfigured) {
      setState((s) => ({ ...s, ready: true }));
      return;
    }
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const claims = session.tokens?.idToken?.payload ?? {};
      const email = typeof claims.email === 'string' ? claims.email : undefined;
      const groups = parseGroups(claims['cognito:groups']);
      writeSnapshot({ email, groups });
      setState({
        ready: true,
        user,
        email,
        groups,
        idToken: session.tokens?.idToken?.toString(),
        challenge: undefined,
      });
    } catch {
      writeSnapshot(undefined);
      setState({
        ready: true,
        user: undefined,
        email: undefined,
        groups: [],
        idToken: undefined,
        challenge: undefined,
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn: AuthApi['signIn'] = useCallback(async (email, password) => {
    const res = await amplifySignIn({ username: email, password });
    if (res.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      setState((s) => ({ ...s, challenge: 'NEW_PASSWORD_REQUIRED' }));
      return { challenge: 'NEW_PASSWORD_REQUIRED' };
    }
    if (res.isSignedIn) {
      await refresh();
    }
    return {};
  }, [refresh]);

  const completeNewPassword: AuthApi['completeNewPassword'] = useCallback(
    async (newPassword) => {
      await confirmSignIn({ challengeResponse: newPassword });
      setState((s) => ({ ...s, challenge: undefined }));
      await refresh();
    },
    [refresh],
  );

  const signUp: AuthApi['signUp'] = useCallback(async (email, password) => {
    const res = await amplifySignUp({
      username: email,
      password,
      options: { userAttributes: { email } },
    });
    return { needsConfirmation: !res.isSignUpComplete };
  }, []);

  const confirmSignUpFn: AuthApi['confirmSignUp'] = useCallback(async (email, code) => {
    await confirmSignUp({ username: email, confirmationCode: code });
  }, []);

  const signOut: AuthApi['signOut'] = useCallback(async () => {
    writeSnapshot(undefined);
    await amplifySignOut();
    await refresh();
  }, [refresh]);

  const value = useMemo<AuthApi>(
    () => ({
      ...state,
      signIn,
      completeNewPassword,
      signUp,
      confirmSignUp: confirmSignUpFn,
      signOut,
      refresh,
    }),
    [state, signIn, completeNewPassword, signUp, confirmSignUpFn, signOut, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function hasRole(groups: Role[], ...allowed: Role[]): boolean {
  return allowed.some((r) => groups.includes(r));
}
